<script setup lang="ts">
import {ExtendedProject} from "@/types";
import {computed, ref} from "vue";
import {router} from "@inertiajs/vue3";
import {useCurrentUser} from "@/composables/user";
import TextField from "@/components/forms/TextField.vue";

interface Props {
  project: ExtendedProject
}
const props = defineProps<Props>()

const isDialogOpen = ref(false)
const isLoading = ref(false)
const username = ref('')

const remove = () => {
    router.delete(route('projects.tokens.destroy-all', {project: props.project.slug}), {
        onStart: () => {
            isLoading.value = true;
        },
        onFinish: () => {
            isLoading.value = false;
        },
        onSuccess: () => {
            isDialogOpen.value = false
        }
    })
}

const isUsernameConfirmed = computed(() => {
    return useCurrentUser.value.name === username.value;
})
</script>

<template>
    <v-dialog
        v-model="isDialogOpen"
        max-width="500"
        overlay-opacity="0.4"
    >
        <template #activator="{ props: attrs }">
            <default-btn
                v-bind="attrs"
                size="small"
                variant="text"
                rounded="lg"
                class="font-weight-medium text-error rounded border bg-grey-lighten-5"
            >
                Revoke all
            </default-btn>
        </template>

        <v-card
            :flat="true"
            rounded="lg"
        >
            <v-card-title class="font-weight-bold border-bottom text-body-2 pt-4">
                Are you sure you want to revoke access?
                <v-btn
                    variant="plain"
                    size="small"
                    icon="mdi-close"
                    class="float-end pb-2"
                    @click="isDialogOpen = false"
                />
            </v-card-title>

            <v-card-text class="pt-3 text-black bg-amber-lighten-4">
                <p class="my-5 text-body-2">
                    This will revoke access for <b>all personal access tokens</b>. This action cannot be undone.
                </p>
            </v-card-text>

            <v-card-text class="mt-5">
                <text-field
                    v-model="username"
                    label="Type your username to confirm"
                />
            </v-card-text>

            <v-divider />

            <v-card-actions class="px-5 py-4">
                <default-btn
                    variant="text"
                    class="text-error border"
                    rounded="lg"
                    :block="true"
                    :loading="isLoading"
                    :disabled="!isUsernameConfirmed"
                    @click="remove"
                >
                    I understand, delete all tokens
                </default-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
:deep(.v-dialog--active) {
  border-radius: 16px !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.btn-outlined {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
